import React, { useState, useEffect } from 'react';
import { useIsMobile } from '../hooks/useIsMobile';
import { Link } from 'react-router-dom';
import {
  Package, ArrowLeft, Book, Search, ChevronRight, ChevronDown,
  LayoutDashboard, PackageSearch, DollarSign, CreditCard, Truck,
  Lock, FileText, Settings, Crown, HelpCircle, Users, BarChart3,
  Plus, Edit3, Trash2, Download, Upload, Camera, Tag, Archive,
  Bell, Shield, Mail, MessageSquare, Zap, Home, Phone, Key,
  RefreshCw, AlertCircle, CheckCircle, XCircle, Info, ExternalLink,
  Smartphone, QrCode, Layers, Filter, Copy, FileSpreadsheet
} from 'lucide-react';


const DocsPage = () => {
  const isMobile = useIsMobile();
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSections, setExpandedSections] = useState(['welcome']);
  const [activeDoc, setActiveDoc] = useState('overview');

  useEffect(() => {
    document.title = 'Documentation | Nexus';
  }, []);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev =>
      prev.includes(sectionId)
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const docSections = [
    {
      id: 'welcome',
      title: 'Welcome',
      icon: Home,
      items: [
        { id: 'overview', title: 'Overview' },
        { id: 'quick-start', title: 'Quick Start Guide' },
        { id: 'free-vs-pro', title: 'Free vs Pro Plans' },
      ]
    },
    {
      id: 'core-features',
      title: 'Core Features',
      icon: LayoutDashboard,
      items: [
        { id: 'dashboard', title: 'Dashboard Overview' },
        { id: 'inventory', title: 'Managing Inventory' },
        { id: 'adding-items', title: '  Adding Items' },
        { id: 'item-statuses', title: '  Item Statuses' },
        { id: 'product-scanner', title: '  Product Scanner' },
        { id: 'bulk-operations', title: '  Bulk Operations' },
        { id: 'sales', title: 'Recording Sales' },
        { id: 'marking-sold', title: '  Marking Items Sold' },
        { id: 'tracking-fees', title: '  Tracking Fees' },
        { id: 'platform-integration', title: '  Platform Integration' },
        { id: 'expenses', title: 'Tracking Expenses' },
        { id: 'adding-expenses', title: '  Adding Expenses' },
        { id: 'expense-categories', title: '  Categories' },
      ]
    },
    {
      id: 'pro-features',
      title: 'Pro Features',
      icon: Crown,
      items: [
        { id: 'packages', title: 'Package Tracking' },
        { id: 'supported-carriers', title: '  Supported Carriers' },
        { id: 'auto-updates', title: '  Auto-Updates' },
        { id: 'vault', title: 'Vault (Personal Collection)' },
        { id: 'reports', title: 'Reports & Analytics' },
        { id: 'sales-reports', title: '  Sales Reports' },
        { id: 'profit-analysis', title: '  Profit Analysis' },
        { id: 'inventory-reports', title: '  Inventory Reports' },
        { id: 'exports', title: 'Data Export' },
        { id: 'csv-export', title: '  CSV Export' },
        { id: 'pdf-reports', title: '  PDF Reports' },
      ]
    },
    {
      id: 'account-settings',
      title: 'Account & Settings',
      icon: Settings,
      items: [
        { id: 'profile', title: 'Profile Settings' },
        { id: 'subscription', title: 'Subscription Management' },
        { id: 'upgrading', title: '  Upgrading to Pro' },
        { id: 'billing', title: '  Managing Billing' },
        { id: 'cancellation', title: '  Cancellation' },
        { id: 'security', title: 'Security' },
        { id: 'password', title: '  Password' },
        { id: 'mfa', title: '  Two-Factor Auth (MFA)' },
      ]
    },
    {
      id: 'faq',
      title: 'FAQ',
      icon: HelpCircle,
      items: [
        { id: 'faq-general', title: 'General Questions' },
        { id: 'faq-billing', title: 'Billing & Subscriptions' },
        { id: 'faq-troubleshooting', title: 'Troubleshooting' },
      ]
    },
    {
      id: 'contact',
      title: 'Contact Support',
      icon: MessageSquare,
      items: [
        { id: 'contact-support', title: 'Get Help' },
      ]
    }
  ];

  // Helper component for Pro badge
  const ProBadge = () => (
    <div className="flex items-center gap-2 mb-4">
      <Crown className="w-5 h-5" style={{ color: '#f59e0b' }} />
      <span className="text-sm font-medium px-2 py-0.5 rounded" style={{ backgroundColor: 'rgba(245, 158, 11, 0.2)', color: '#f59e0b' }}>Pro Feature</span>
    </div>
  );

  // Helper component for info box
  const InfoBox = ({ type = 'info', children }) => {
    const styles = {
      info: { bg: 'rgba(59, 130, 246, 0.1)', border: 'rgba(59, 130, 246, 0.2)', icon: Info, color: '#3b82f6' },
      success: { bg: 'rgba(34, 197, 94, 0.1)', border: 'rgba(34, 197, 94, 0.2)', icon: CheckCircle, color: '#22c55e' },
      warning: { bg: 'rgba(245, 158, 11, 0.1)', border: 'rgba(245, 158, 11, 0.2)', icon: AlertCircle, color: '#f59e0b' },
      error: { bg: 'rgba(239, 68, 68, 0.1)', border: 'rgba(239, 68, 68, 0.2)', icon: XCircle, color: '#ef4444' },
    };
    const style = styles[type];
    const Icon = style.icon;

    return (
      <div className="p-4 rounded-xl mb-6" style={{ backgroundColor: style.bg, border: `1px solid ${style.border}` }}>
        <div className="flex items-start gap-3">
          <Icon className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: style.color }} />
          <div className="text-sm" style={{ color: '#94a3b8' }}>{children}</div>
        </div>
      </div>
    );
  };

  // Helper for step lists
  const StepList = ({ steps, color = '#3b82f6' }) => (
    <div className="space-y-4 mb-8">
      {steps.map((step, idx) => (
        <div key={idx} className="flex gap-4">
          <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 font-bold" style={{ backgroundColor: `${color}20`, color }}>
            {idx + 1}
          </div>
          <div>
            {step.title && <h4 className="font-semibold mb-1">{step.title}</h4>}
            <p className="text-sm" style={{ color: '#94a3b8' }}>{step.desc || step}</p>
          </div>
        </div>
      ))}
    </div>
  );

  // FAQ Item component
  const FAQItem = ({ question, answer }) => {
    const [isOpen, setIsOpen] = useState(false);
    return (
      <div className="rounded-xl mb-3" style={{ backgroundColor: 'rgba(15, 30, 50, 0.5)', border: '1px solid rgba(59, 130, 246, 0.1)' }}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center justify-between p-4 text-left"
          style={{ color: '#fff' }}
        >
          <span className="font-medium pr-4">{question}</span>
          <ChevronDown
            className="w-5 h-5 flex-shrink-0 transition-transform"
            style={{ color: '#64748b', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
          />
        </button>
        {isOpen && (
          <div className="px-4 pb-4 text-sm" style={{ color: '#94a3b8' }}>
            {answer}
          </div>
        )}
      </div>
    );
  };

  const docContent = {
    // ==================== WELCOME ====================
    'overview': {
      title: 'Overview',
      icon: Book,
      content: (
        <>
          <p className="text-lg mb-6" style={{ color: '#94a3b8' }}>
            Welcome to Nexus! Nexus is your all-in-one inventory management system designed for resellers,
            collectors, and small business owners to track their inventory, sales, and expenses.
          </p>

          <h3 className="text-xl font-semibold mb-4">What can you do with Nexus?</h3>
          <ul className="space-y-3 mb-8">
            {[
              'Track your inventory with detailed item information including photos, pricing, and condition',
              'Record sales and monitor your profit margins across multiple platforms',
              'Log expenses to understand your true costs and maximize profitability',
              'Manage buyers and track customer purchase history',
              'Track packages with real-time shipping updates (Pro)',
              'Store personal collection items in the Vault (Pro)',
              'Generate detailed reports and export your data (Pro)',
              'Create year-end archives for tax and record keeping (Pro)',
            ].map((item, idx) => (
              <li key={idx} className="flex items-start gap-3">
                <ChevronRight className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#3b82f6' }} />
                <span style={{ color: '#94a3b8' }}>{item}</span>
              </li>
            ))}
          </ul>

          <h3 className="text-xl font-semibold mb-4">Built for Resellers</h3>
          <p className="mb-6" style={{ color: '#94a3b8' }}>
            Whether you're flipping sneakers, selling trading cards, reselling electronics, or running a small business,
            Nexus provides the tools you need to stay organized and profitable. Track items across multiple selling platforms
            including eBay, StockX, GOAT, Mercari, Poshmark, Facebook Marketplace, and more.
          </p>

          <InfoBox type="info">
            <p className="font-medium mb-1">Need help?</p>
            <p>
              If you have questions or run into issues, visit our <Link to="/support" className="underline" style={{ color: '#60a5fa' }}>Support page</Link> or
              email us at <a href="mailto:support@nexusmanager.app" className="underline" style={{ color: '#60a5fa' }}>support@nexusmanager.app</a>.
            </p>
          </InfoBox>
        </>
      )
    },
    'quick-start': {
      title: 'Quick Start Guide',
      icon: Zap,
      content: (
        <>
          <p className="mb-6" style={{ color: '#94a3b8' }}>
            Get up and running with Nexus in just a few minutes. Follow this guide to start tracking your inventory today.
          </p>

          <h3 className="text-xl font-semibold mb-4">Step 1: Create Your Account</h3>
          <StepList steps={[
            { title: 'Visit the Homepage', desc: 'Go to nexusmanager.app and click "Get Started Free".' },
            { title: 'Sign Up', desc: 'Enter your email and create a secure password, or sign up with Google or Discord.' },
            { title: 'Verify Your Email', desc: 'Check your inbox for a verification email and click the confirmation link.' },
          ]} color="#3b82f6" />

          <h3 className="text-xl font-semibold mb-4">Step 2: Add Your First Item</h3>
          <StepList steps={[
            { title: 'Go to Inventory', desc: 'Navigate to the Inventory page from the sidebar.' },
            { title: 'Click "Add Item"', desc: 'Open the add item form to enter your product details.' },
            { title: 'Fill in Details', desc: 'Add the item name, purchase price, condition, and optionally upload photos.' },
            { title: 'Save', desc: 'Click Save to add the item to your inventory.' },
          ]} color="#22c55e" />

          <h3 className="text-xl font-semibold mb-4">Step 3: Record Your First Sale</h3>
          <StepList steps={[
            { title: 'Find the Item', desc: 'Locate the item you sold in your inventory.' },
            { title: 'Mark as Sold', desc: 'Click on the item and change its status to "Sold".' },
            { title: 'Enter Sale Details', desc: 'Add the sale price, date, platform, and any fees.' },
            { title: 'View Your Profit', desc: 'Check the Sales page to see your profit calculations.' },
          ]} color="#8b5cf6" />

          <InfoBox type="success">
            <p className="font-medium mb-1">You're all set!</p>
            <p>Continue adding items, recording sales, and tracking expenses to get a complete picture of your business performance.</p>
          </InfoBox>
        </>
      )
    },
    'free-vs-pro': {
      title: 'Free vs Pro Plans',
      icon: Crown,
      content: (
        <>
          <p className="mb-6" style={{ color: '#94a3b8' }}>
            Nexus offers a generous free plan with the ability to upgrade to Pro for unlimited access and additional features.
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* Free Plan */}
            <div className="p-6 rounded-xl" style={{ backgroundColor: 'rgba(15, 30, 50, 0.5)', border: '1px solid rgba(59, 130, 246, 0.15)' }}>
              <h4 className="text-lg font-semibold mb-4">Free Plan</h4>
              <ul className="space-y-3">
                {[
                  'Up to 150 inventory items',
                  'Up to 50 sales records',
                  'Up to 50 expense records',
                  'Dashboard with charts and stats',
                  'Product barcode scanner',
                  'Buyer management',
                  'Basic CSV import',
                  'Mobile-responsive design',
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm" style={{ color: '#94a3b8' }}>
                    <CheckCircle className="w-4 h-4" style={{ color: '#22c55e' }} />
                    {item}
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-lg font-semibold" style={{ color: '#22c55e' }}>Free forever</p>
            </div>

            {/* Pro Plan */}
            <div className="p-6 rounded-xl" style={{ background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(29, 78, 216, 0.15) 100%)', border: '1px solid rgba(59, 130, 246, 0.3)' }}>
              <div className="flex items-center gap-2 mb-4">
                <h4 className="text-lg font-semibold">Pro Plan</h4>
                <Crown className="w-5 h-5" style={{ color: '#f59e0b' }} />
              </div>
              <ul className="space-y-3">
                {[
                  'Unlimited inventory items',
                  'Unlimited sales & expenses',
                  'Real-time package tracking',
                  'Vault for personal collection',
                  'Advanced reports & analytics',
                  'Data export (CSV, PDF, JSON)',
                  'Year-end archives',
                  'Full account data backup',
                  'Priority support',
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm" style={{ color: '#94a3b8' }}>
                    <Crown className="w-4 h-4" style={{ color: '#f59e0b' }} />
                    {item}
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-lg font-semibold" style={{ color: '#60a5fa' }}>$15/month</p>
            </div>
          </div>

          <InfoBox type="info">
            <p>You can upgrade to Pro at any time from your Account Settings. Your subscription can be canceled anytime, and you'll retain access until the end of your billing period.</p>
          </InfoBox>
        </>
      )
    },

    // ==================== CORE FEATURES ====================
    'dashboard': {
      title: 'Dashboard Overview',
      icon: LayoutDashboard,
      content: (
        <>
          <p className="mb-6" style={{ color: '#94a3b8' }}>
            The Dashboard is your command center, giving you a quick overview of your business performance at a glance.
          </p>

          <h3 className="text-xl font-semibold mb-4">Key Metrics</h3>
          <div className="grid md:grid-cols-2 gap-4 mb-8">
            {[
              { title: 'Total Inventory', desc: 'Number of items currently in your inventory and their total value.' },
              { title: 'Total Sales', desc: 'Sum of all your recorded sales with profit calculations.' },
              { title: 'Total Expenses', desc: 'Track all business expenses to understand true costs.' },
              { title: 'Net Profit', desc: 'Your actual profit after all costs and fees are deducted.' },
            ].map((item, idx) => (
              <div key={idx} className="p-4 rounded-xl" style={{ backgroundColor: 'rgba(15, 30, 50, 0.5)', border: '1px solid rgba(59, 130, 246, 0.1)' }}>
                <h4 className="font-semibold mb-2">{item.title}</h4>
                <p className="text-sm" style={{ color: '#94a3b8' }}>{item.desc}</p>
              </div>
            ))}
          </div>

          <h3 className="text-xl font-semibold mb-4">Visual Charts</h3>
          <p className="mb-4" style={{ color: '#94a3b8' }}>
            Your dashboard includes interactive charts showing:
          </p>
          <ul className="space-y-2 mb-6">
            {[
              'Sales trends over time (daily, weekly, monthly)',
              'Expense breakdown by category',
              'Profit margins and growth',
              'Inventory aging and turnover',
            ].map((item, idx) => (
              <li key={idx} className="flex items-center gap-2 text-sm" style={{ color: '#94a3b8' }}>
                <BarChart3 className="w-4 h-4" style={{ color: '#3b82f6' }} />
                {item}
              </li>
            ))}
          </ul>

          <h3 className="text-xl font-semibold mb-4">Quick Stats Cards</h3>
          <p style={{ color: '#94a3b8' }}>
            The dashboard also shows quick stats for renewals (recurring expenses due soon),
            active packages being tracked, and vault item counts for Pro users.
          </p>
        </>
      )
    },
    'inventory': {
      title: 'Managing Inventory',
      icon: PackageSearch,
      content: (
        <>
          <p className="mb-6" style={{ color: '#94a3b8' }}>
            The Inventory page is the heart of Nexus. Here you manage all your products, track their status,
            and maintain complete records of your stock.
          </p>

          <h3 className="text-xl font-semibold mb-4">Inventory Features</h3>
          <div className="grid md:grid-cols-2 gap-4 mb-8">
            {[
              { icon: Plus, title: 'Add Items', desc: 'Quickly add new items with detailed information.' },
              { icon: Edit3, title: 'Edit Items', desc: 'Update item details, prices, and photos anytime.' },
              { icon: Filter, title: 'Filter & Sort', desc: 'Find items by status, category, date, or custom filters.' },
              { icon: Layers, title: 'Bulk Edit', desc: 'Edit multiple items at once to save time.' },
              { icon: QrCode, title: 'Barcode Scanner', desc: 'Scan barcodes to quickly look up or add products.' },
              { icon: Upload, title: 'CSV Import', desc: 'Import inventory from spreadsheets.' },
            ].map((item, idx) => (
              <div key={idx} className="flex items-start gap-3 p-4 rounded-xl" style={{ backgroundColor: 'rgba(15, 30, 50, 0.5)', border: '1px solid rgba(59, 130, 246, 0.1)' }}>
                <item.icon className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#3b82f6' }} />
                <div>
                  <h4 className="font-semibold mb-1">{item.title}</h4>
                  <p className="text-sm" style={{ color: '#94a3b8' }}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <h3 className="text-xl font-semibold mb-4">Item Details</h3>
          <p className="mb-4" style={{ color: '#94a3b8' }}>
            Each inventory item can include:
          </p>
          <ul className="space-y-2 mb-6">
            {[
              'Name, SKU, and serial number',
              'Brand, color, and size (with size type for shoes)',
              'Condition (DS, VNDS, PADS, Used) and box condition',
              'Purchase price, listing price, and quantity',
              'Purchase date and platform',
              'Photos and receipt/invoice uploads',
              'Grading information for trading cards (PSA, BGS, CGC, etc.)',
              'Notes and custom fields',
            ].map((item, idx) => (
              <li key={idx} className="flex items-center gap-2 text-sm" style={{ color: '#94a3b8' }}>
                <ChevronRight className="w-4 h-4" style={{ color: '#22c55e' }} />
                {item}
              </li>
            ))}
          </ul>
        </>
      )
    },
    'adding-items': {
      title: 'Adding Items',
      icon: Plus,
      content: (
        <>
          <p className="mb-6" style={{ color: '#94a3b8' }}>
            Learn how to add items to your inventory quickly and efficiently.
          </p>

          <h3 className="text-xl font-semibold mb-4">Manual Entry</h3>
          <StepList steps={[
            { title: 'Click "Add Item"', desc: 'Find the Add Item button on the Inventory page.' },
            { title: 'Enter Basic Info', desc: 'Add the item name, and optionally SKU, brand, and serial number.' },
            { title: 'Set Pricing', desc: 'Enter your purchase price and listing/expected selling price.' },
            { title: 'Choose Condition', desc: 'Select the item condition: Deadstock (DS), Very Near Deadstock (VNDS), Pass as Deadstock (PADS), or Used.' },
            { title: 'Add Photos', desc: 'Upload photos of your item for easy identification.' },
            { title: 'Save', desc: 'Click Save to add the item to your inventory.' },
          ]} color="#22c55e" />

          <h3 className="text-xl font-semibold mb-4">Using the Barcode Scanner</h3>
          <StepList steps={[
            { title: 'Click Scan Barcode', desc: 'Open the scanner from the Inventory page.' },
            { title: 'Allow Camera Access', desc: 'Grant permission for the app to use your camera.' },
            { title: 'Scan the Barcode', desc: 'Point your camera at the product barcode.' },
            { title: 'Review & Save', desc: 'The product details will auto-fill. Review and save.' },
          ]} color="#3b82f6" />

          <h3 className="text-xl font-semibold mb-4">CSV Import</h3>
          <StepList steps={[
            { title: 'Download Template', desc: 'Get the CSV template from the Import dialog.' },
            { title: 'Fill in Your Data', desc: 'Add your items to the spreadsheet following the template format.' },
            { title: 'Upload CSV', desc: 'Click Import and select your completed CSV file.' },
            { title: 'Map Columns', desc: 'Verify the column mapping and import your items.' },
          ]} color="#8b5cf6" />

          <InfoBox type="info">
            <p className="font-medium mb-1">Tip</p>
            <p>Use consistent naming conventions for your items to make searching and filtering easier later.</p>
          </InfoBox>
        </>
      )
    },
    'item-statuses': {
      title: 'Item Statuses',
      icon: Tag,
      content: (
        <>
          <p className="mb-6" style={{ color: '#94a3b8' }}>
            Items in your inventory can have different statuses to help you track where they are in the selling process.
          </p>

          <h3 className="text-xl font-semibold mb-4">Available Statuses</h3>
          <div className="space-y-4 mb-8">
            {[
              { status: 'Unlisted', color: '#64748b', desc: 'Item is in your inventory but not yet listed for sale on any platform.' },
              { status: 'Listed', color: '#3b82f6', desc: 'Item is actively listed for sale on one or more platforms.' },
              { status: 'Sold', color: '#22c55e', desc: 'Item has been sold. This triggers the sale recording process.' },
              { status: 'Shipped', color: '#8b5cf6', desc: 'Item has been sold and shipped to the buyer.' },
            ].map((item) => (
              <div key={item.status} className="flex items-start gap-4 p-4 rounded-xl" style={{ backgroundColor: 'rgba(15, 30, 50, 0.5)', border: '1px solid rgba(59, 130, 246, 0.1)' }}>
                <div className="px-3 py-1 rounded-full text-sm font-medium flex-shrink-0" style={{ backgroundColor: `${item.color}20`, color: item.color }}>
                  {item.status}
                </div>
                <p className="text-sm" style={{ color: '#94a3b8' }}>{item.desc}</p>
              </div>
            ))}
          </div>

          <h3 className="text-xl font-semibold mb-4">Age Indicators</h3>
          <p className="mb-4" style={{ color: '#94a3b8' }}>
            Items also display age badges to help you identify how long they've been in inventory:
          </p>
          <div className="flex flex-wrap gap-3 mb-6">
            <span className="px-3 py-1 rounded-full text-sm" style={{ backgroundColor: 'rgba(34, 197, 94, 0.2)', color: '#22c55e' }}>New (0-7 days)</span>
            <span className="px-3 py-1 rounded-full text-sm" style={{ backgroundColor: 'rgba(245, 158, 11, 0.2)', color: '#f59e0b' }}>7-30 days</span>
            <span className="px-3 py-1 rounded-full text-sm" style={{ backgroundColor: 'rgba(239, 68, 68, 0.2)', color: '#ef4444' }}>30+ days</span>
          </div>

          <InfoBox type="warning">
            <p>Items that have been in inventory for a long time may need a price adjustment or marketing push to sell.</p>
          </InfoBox>
        </>
      )
    },
    'product-scanner': {
      title: 'Product Scanner',
      icon: QrCode,
      content: (
        <>
          <p className="mb-6" style={{ color: '#94a3b8' }}>
            Use the built-in barcode scanner to quickly look up products and add them to your inventory.
          </p>

          <h3 className="text-xl font-semibold mb-4">How It Works</h3>
          <StepList steps={[
            { title: 'Open Scanner', desc: 'Click the Scan button on the Inventory page or Product Scanner page.' },
            { title: 'Point at Barcode', desc: 'Hold your device camera steady over the product barcode or QR code.' },
            { title: 'Auto-Detection', desc: 'The scanner automatically detects and reads the barcode.' },
            { title: 'Product Lookup', desc: 'If the product exists in the database, details are auto-filled.' },
          ]} color="#3b82f6" />

          <h3 className="text-xl font-semibold mb-4">Supported Barcode Types</h3>
          <ul className="space-y-2 mb-6">
            {[
              'UPC (Universal Product Code)',
              'EAN (European Article Number)',
              'QR Codes',
              'Code 128',
              'Code 39',
            ].map((item, idx) => (
              <li key={idx} className="flex items-center gap-2 text-sm" style={{ color: '#94a3b8' }}>
                <QrCode className="w-4 h-4" style={{ color: '#3b82f6' }} />
                {item}
              </li>
            ))}
          </ul>

          <h3 className="text-xl font-semibold mb-4">Manual Barcode Entry</h3>
          <p className="mb-4" style={{ color: '#94a3b8' }}>
            If your camera isn't working or you prefer manual entry, you can type the barcode number directly into the search field.
          </p>

          <InfoBox type="info">
            <p className="font-medium mb-1">Camera Permissions</p>
            <p>Make sure to allow camera access when prompted. On iOS devices, you may need to enable camera permissions in your browser settings.</p>
          </InfoBox>
        </>
      )
    },
    'bulk-operations': {
      title: 'Bulk Operations',
      icon: Layers,
      content: (
        <>
          <p className="mb-6" style={{ color: '#94a3b8' }}>
            Save time by performing actions on multiple items at once with bulk operations.
          </p>

          <h3 className="text-xl font-semibold mb-4">Bulk Edit</h3>
          <StepList steps={[
            { title: 'Select Items', desc: 'Use the checkboxes to select multiple items in your inventory.' },
            { title: 'Click Bulk Edit', desc: 'The Bulk Edit button appears when items are selected.' },
            { title: 'Choose Fields', desc: 'Select which fields you want to update (condition, size, status, etc.).' },
            { title: 'Apply Changes', desc: 'Enter the new values and apply to all selected items.' },
          ]} color="#8b5cf6" />

          <h3 className="text-xl font-semibold mb-4">Available Bulk Actions</h3>
          <div className="grid md:grid-cols-2 gap-4 mb-8">
            {[
              { icon: Edit3, title: 'Bulk Edit', desc: 'Update condition, size, grading, and other fields.' },
              { icon: Trash2, title: 'Bulk Delete', desc: 'Remove multiple items at once.' },
              { icon: Archive, title: 'Bulk Archive', desc: 'Move items to vault or archive.' },
              { icon: Copy, title: 'Duplicate', desc: 'Create copies of selected items.' },
            ].map((item, idx) => (
              <div key={idx} className="flex items-start gap-3 p-4 rounded-xl" style={{ backgroundColor: 'rgba(15, 30, 50, 0.5)', border: '1px solid rgba(59, 130, 246, 0.1)' }}>
                <item.icon className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#8b5cf6' }} />
                <div>
                  <h4 className="font-semibold mb-1">{item.title}</h4>
                  <p className="text-sm" style={{ color: '#94a3b8' }}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <InfoBox type="warning">
            <p className="font-medium mb-1">Careful with Bulk Delete</p>
            <p>Bulk delete is permanent. Make sure you've selected the correct items before confirming deletion.</p>
          </InfoBox>
        </>
      )
    },
    'sales': {
      title: 'Recording Sales',
      icon: DollarSign,
      content: (
        <>
          <p className="mb-6" style={{ color: '#94a3b8' }}>
            Track your sales to monitor revenue, calculate profits, and understand your business performance.
          </p>

          <h3 className="text-xl font-semibold mb-4">Sales Features</h3>
          <div className="grid md:grid-cols-2 gap-4 mb-8">
            {[
              { icon: DollarSign, title: 'Profit Calculation', desc: 'Automatic profit calculation based on purchase price, sale price, and fees.' },
              { icon: Users, title: 'Buyer Tracking', desc: 'Link sales to buyers for customer relationship management.' },
              { icon: Filter, title: 'Filter & Sort', desc: 'View sales by date, platform, profit, and more.' },
              { icon: Download, title: 'Export', desc: 'Export sales data to CSV or PDF (Pro).' },
            ].map((item, idx) => (
              <div key={idx} className="flex items-start gap-3 p-4 rounded-xl" style={{ backgroundColor: 'rgba(15, 30, 50, 0.5)', border: '1px solid rgba(59, 130, 246, 0.1)' }}>
                <item.icon className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#22c55e' }} />
                <div>
                  <h4 className="font-semibold mb-1">{item.title}</h4>
                  <p className="text-sm" style={{ color: '#94a3b8' }}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <h3 className="text-xl font-semibold mb-4">Viewing Sales</h3>
          <p style={{ color: '#94a3b8' }}>
            The Sales page shows all your completed sales with key information including item name,
            sale price, profit, platform, buyer, and date. Use filters to analyze sales by time period,
            platform, or profit margin.
          </p>
        </>
      )
    },
    'marking-sold': {
      title: 'Marking Items Sold',
      icon: CheckCircle,
      content: (
        <>
          <p className="mb-6" style={{ color: '#94a3b8' }}>
            When you sell an item, mark it as sold to record the sale and update your inventory.
          </p>

          <h3 className="text-xl font-semibold mb-4">How to Mark an Item Sold</h3>
          <StepList steps={[
            { title: 'Find the Item', desc: 'Locate the item in your inventory using search or filters.' },
            { title: 'Open Item Details', desc: 'Click on the item to view its details.' },
            { title: 'Change Status', desc: 'Change the status from "Listed" to "Sold".' },
            { title: 'Enter Sale Info', desc: 'Add the sale price, sale date, platform, and buyer (optional).' },
            { title: 'Add Fees', desc: 'Include any platform fees, shipping costs, or other deductions.' },
            { title: 'Save', desc: 'Confirm the sale to create a sales record.' },
          ]} color="#22c55e" />

          <h3 className="text-xl font-semibold mb-4">What Happens When You Mark Sold</h3>
          <ul className="space-y-2 mb-6">
            {[
              'The item moves from active inventory to sold items',
              'A sales record is created with profit calculations',
              'Dashboard stats are updated automatically',
              'The sale appears in your Sales page',
            ].map((item, idx) => (
              <li key={idx} className="flex items-center gap-2 text-sm" style={{ color: '#94a3b8' }}>
                <ChevronRight className="w-4 h-4" style={{ color: '#22c55e' }} />
                {item}
              </li>
            ))}
          </ul>
        </>
      )
    },
    'tracking-fees': {
      title: 'Tracking Fees',
      icon: CreditCard,
      content: (
        <>
          <p className="mb-6" style={{ color: '#94a3b8' }}>
            Accurately tracking fees is essential for understanding your true profit margins.
          </p>

          <h3 className="text-xl font-semibold mb-4">Types of Fees</h3>
          <div className="space-y-4 mb-8">
            {[
              { title: 'Platform Fees', desc: 'Fees charged by selling platforms (eBay, StockX, Mercari, etc.). Usually a percentage of the sale price.' },
              { title: 'Payment Processing', desc: 'Fees for payment processing (PayPal, Stripe, etc.). Typically 2.9% + $0.30.' },
              { title: 'Shipping Costs', desc: 'Cost to ship the item to the buyer if you covered shipping.' },
              { title: 'Authentication Fees', desc: 'Fees for authentication services on platforms like StockX or GOAT.' },
            ].map((item, idx) => (
              <div key={idx} className="p-4 rounded-xl" style={{ backgroundColor: 'rgba(15, 30, 50, 0.5)', border: '1px solid rgba(59, 130, 246, 0.1)' }}>
                <h4 className="font-semibold mb-1">{item.title}</h4>
                <p className="text-sm" style={{ color: '#94a3b8' }}>{item.desc}</p>
              </div>
            ))}
          </div>

          <h3 className="text-xl font-semibold mb-4">Profit Calculation</h3>
          <div className="p-4 rounded-xl mb-6" style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.2)' }}>
            <p className="font-mono text-sm" style={{ color: '#94a3b8' }}>
              <strong style={{ color: '#22c55e' }}>Net Profit</strong> = Sale Price - Purchase Price - All Fees
            </p>
          </div>

          <InfoBox type="info">
            <p className="font-medium mb-1">Tip</p>
            <p>Recording accurate fees helps you understand which platforms are most profitable and where you might be losing money.</p>
          </InfoBox>
        </>
      )
    },
    'platform-integration': {
      title: 'Platform Integration',
      icon: ExternalLink,
      content: (
        <>
          <p className="mb-6" style={{ color: '#94a3b8' }}>
            Nexus supports tracking sales across all major reselling platforms.
          </p>

          <h3 className="text-xl font-semibold mb-4">Supported Platforms</h3>
          <div className="flex flex-wrap gap-2 mb-8">
            {['eBay', 'StockX', 'GOAT', 'Mercari', 'Poshmark', 'Facebook Marketplace', 'OfferUp', 'Depop', 'Grailed', 'Whatnot', 'Instagram', 'Discord', 'In Person'].map((platform) => (
              <span key={platform} className="px-3 py-1 rounded-full text-sm" style={{ backgroundColor: 'rgba(59, 130, 246, 0.15)', color: '#60a5fa' }}>
                {platform}
              </span>
            ))}
          </div>

          <h3 className="text-xl font-semibold mb-4">Platform-Specific Features</h3>
          <ul className="space-y-2 mb-6">
            {[
              'Track which platforms generate the most sales',
              'Compare profit margins across platforms',
              'Record platform-specific fees accurately',
              'Link buyers to their preferred platform',
            ].map((item, idx) => (
              <li key={idx} className="flex items-center gap-2 text-sm" style={{ color: '#94a3b8' }}>
                <ChevronRight className="w-4 h-4" style={{ color: '#3b82f6' }} />
                {item}
              </li>
            ))}
          </ul>
        </>
      )
    },
    'expenses': {
      title: 'Tracking Expenses',
      icon: CreditCard,
      content: (
        <>
          <p className="mb-6" style={{ color: '#94a3b8' }}>
            Track all your business expenses to understand your true costs and calculate accurate profits.
          </p>

          <h3 className="text-xl font-semibold mb-4">Why Track Expenses?</h3>
          <ul className="space-y-2 mb-8">
            {[
              'Understand your true profit margins',
              'Identify areas where you can cut costs',
              'Prepare for tax season with organized records',
              'Track recurring subscriptions and supplies',
            ].map((item, idx) => (
              <li key={idx} className="flex items-center gap-2 text-sm" style={{ color: '#94a3b8' }}>
                <ChevronRight className="w-4 h-4" style={{ color: '#22c55e' }} />
                {item}
              </li>
            ))}
          </ul>

          <h3 className="text-xl font-semibold mb-4">Expense Features</h3>
          <div className="grid md:grid-cols-2 gap-4 mb-8">
            {[
              { icon: Plus, title: 'Add Expenses', desc: 'Record individual expenses with details.' },
              { icon: RefreshCw, title: 'Recurring Templates', desc: 'Set up templates for recurring expenses.' },
              { icon: Tag, title: 'Categories', desc: 'Organize expenses by category.' },
              { icon: FileSpreadsheet, title: 'Import/Export', desc: 'Bulk import and export expense data.' },
            ].map((item, idx) => (
              <div key={idx} className="flex items-start gap-3 p-4 rounded-xl" style={{ backgroundColor: 'rgba(15, 30, 50, 0.5)', border: '1px solid rgba(59, 130, 246, 0.1)' }}>
                <item.icon className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#f59e0b' }} />
                <div>
                  <h4 className="font-semibold mb-1">{item.title}</h4>
                  <p className="text-sm" style={{ color: '#94a3b8' }}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </>
      )
    },
    'adding-expenses': {
      title: 'Adding Expenses',
      icon: Plus,
      content: (
        <>
          <p className="mb-6" style={{ color: '#94a3b8' }}>
            Learn how to add and manage your business expenses.
          </p>

          <h3 className="text-xl font-semibold mb-4">Adding a New Expense</h3>
          <StepList steps={[
            { title: 'Go to Expenses', desc: 'Navigate to the Expenses page from the sidebar.' },
            { title: 'Click Add Expense', desc: 'Open the expense form.' },
            { title: 'Enter Details', desc: 'Add the expense name, amount, date, and category.' },
            { title: 'Add Receipt', desc: 'Optionally upload a receipt image for your records.' },
            { title: 'Save', desc: 'Save the expense to your records.' },
          ]} color="#f59e0b" />

          <h3 className="text-xl font-semibold mb-4">Recurring Expenses</h3>
          <p className="mb-4" style={{ color: '#94a3b8' }}>
            For expenses that repeat regularly (subscriptions, rent, etc.), you can create recurring expense templates:
          </p>
          <ul className="space-y-2 mb-6">
            {[
              'Create a template with the expense details',
              'Set the frequency (monthly, yearly, etc.)',
              'The system tracks when renewals are due',
              'Quickly add new instances from the template',
            ].map((item, idx) => (
              <li key={idx} className="flex items-center gap-2 text-sm" style={{ color: '#94a3b8' }}>
                <RefreshCw className="w-4 h-4" style={{ color: '#f59e0b' }} />
                {item}
              </li>
            ))}
          </ul>
        </>
      )
    },
    'expense-categories': {
      title: 'Expense Categories',
      icon: Tag,
      content: (
        <>
          <p className="mb-6" style={{ color: '#94a3b8' }}>
            Organize your expenses into categories for better tracking and reporting.
          </p>

          <h3 className="text-xl font-semibold mb-4">Default Categories</h3>
          <div className="grid md:grid-cols-2 gap-4 mb-8">
            {[
              { title: 'Shipping Supplies', desc: 'Boxes, tape, labels, bubble wrap, etc.' },
              { title: 'Platform Fees', desc: 'Monthly platform subscriptions and selling fees.' },
              { title: 'Equipment', desc: 'Camera, lighting, storage, displays.' },
              { title: 'Software', desc: 'Tools, apps, and software subscriptions.' },
              { title: 'Travel', desc: 'Mileage, gas, and travel expenses.' },
              { title: 'Marketing', desc: 'Advertising, promotions, business cards.' },
            ].map((item, idx) => (
              <div key={idx} className="p-4 rounded-xl" style={{ backgroundColor: 'rgba(15, 30, 50, 0.5)', border: '1px solid rgba(59, 130, 246, 0.1)' }}>
                <h4 className="font-semibold mb-1">{item.title}</h4>
                <p className="text-sm" style={{ color: '#94a3b8' }}>{item.desc}</p>
              </div>
            ))}
          </div>

          <h3 className="text-xl font-semibold mb-4">Custom Categories</h3>
          <p style={{ color: '#94a3b8' }}>
            You can create custom expense categories to match your specific business needs.
            Custom categories appear alongside the default categories when adding or filtering expenses.
          </p>
        </>
      )
    },

    // ==================== PRO FEATURES ====================
    'packages': {
      title: 'Package Tracking',
      icon: Truck,
      content: (
        <>
          <ProBadge />
          <p className="mb-6" style={{ color: '#94a3b8' }}>
            Track all your incoming and outgoing shipments in one place with real-time updates from major carriers.
          </p>

          <h3 className="text-xl font-semibold mb-4">Package Tracking Features</h3>
          <div className="grid md:grid-cols-2 gap-4 mb-8">
            {[
              { icon: Truck, title: 'Real-Time Updates', desc: 'Get automatic status updates from carriers.' },
              { icon: RefreshCw, title: 'Auto-Refresh', desc: 'Tracking info updates automatically.' },
              { icon: Upload, title: 'Bulk Add', desc: 'Import multiple tracking numbers at once.' },
              { icon: Bell, title: 'Status History', desc: 'View complete tracking history for each package.' },
            ].map((item, idx) => (
              <div key={idx} className="flex items-start gap-3 p-4 rounded-xl" style={{ backgroundColor: 'rgba(15, 30, 50, 0.5)', border: '1px solid rgba(59, 130, 246, 0.1)' }}>
                <item.icon className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#8b5cf6' }} />
                <div>
                  <h4 className="font-semibold mb-1">{item.title}</h4>
                  <p className="text-sm" style={{ color: '#94a3b8' }}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <h3 className="text-xl font-semibold mb-4">Adding a Package</h3>
          <StepList steps={[
            { title: 'Go to Packages', desc: 'Navigate to the Packages page.' },
            { title: 'Click Add Package', desc: 'Open the add package form.' },
            { title: 'Enter Tracking Number', desc: 'Paste or type the tracking number.' },
            { title: 'Auto-Detect Carrier', desc: 'The carrier is automatically detected from the tracking number.' },
            { title: 'Add Details', desc: 'Optionally add a description and link to an inventory item.' },
          ]} color="#8b5cf6" />
        </>
      )
    },
    'supported-carriers': {
      title: 'Supported Carriers',
      icon: Truck,
      content: (
        <>
          <ProBadge />
          <p className="mb-6" style={{ color: '#94a3b8' }}>
            Nexus supports tracking packages from all major shipping carriers.
          </p>

          <h3 className="text-xl font-semibold mb-4">Major Carriers</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
            {[
              { name: 'USPS', desc: 'United States Postal Service' },
              { name: 'UPS', desc: 'United Parcel Service' },
              { name: 'FedEx', desc: 'Federal Express' },
              { name: 'DHL', desc: 'DHL Express' },
              { name: 'OnTrac', desc: 'Regional carrier' },
              { name: 'LaserShip', desc: 'Regional carrier' },
            ].map((carrier) => (
              <div key={carrier.name} className="p-4 rounded-xl text-center" style={{ backgroundColor: 'rgba(15, 30, 50, 0.5)', border: '1px solid rgba(59, 130, 246, 0.1)' }}>
                <h4 className="font-semibold mb-1">{carrier.name}</h4>
                <p className="text-xs" style={{ color: '#64748b' }}>{carrier.desc}</p>
              </div>
            ))}
          </div>

          <h3 className="text-xl font-semibold mb-4">Carrier Auto-Detection</h3>
          <p style={{ color: '#94a3b8' }}>
            When you enter a tracking number, Nexus automatically detects the carrier based on the
            tracking number format. No need to manually select the carrier.
          </p>
        </>
      )
    },
    'auto-updates': {
      title: 'Auto-Updates',
      icon: RefreshCw,
      content: (
        <>
          <ProBadge />
          <p className="mb-6" style={{ color: '#94a3b8' }}>
            Package tracking information updates automatically so you always have the latest status.
          </p>

          <h3 className="text-xl font-semibold mb-4">How Auto-Updates Work</h3>
          <ul className="space-y-3 mb-8">
            {[
              'Tracking info refreshes periodically in the background',
              'Status changes are reflected automatically',
              'Estimated delivery dates update as packages move',
              'Complete tracking history is preserved',
            ].map((item, idx) => (
              <li key={idx} className="flex items-center gap-3">
                <RefreshCw className="w-4 h-4" style={{ color: '#8b5cf6' }} />
                <span className="text-sm" style={{ color: '#94a3b8' }}>{item}</span>
              </li>
            ))}
          </ul>

          <h3 className="text-xl font-semibold mb-4">Manual Refresh</h3>
          <p style={{ color: '#94a3b8' }}>
            You can also manually refresh tracking information by clicking the refresh button on any package.
          </p>
        </>
      )
    },
    'vault': {
      title: 'Vault (Personal Collection)',
      icon: Lock,
      content: (
        <>
          <ProBadge />
          <p className="mb-6" style={{ color: '#94a3b8' }}>
            The Vault is your personal collection tracker. Store items you want to keep separate from your reselling inventory.
          </p>

          <h3 className="text-xl font-semibold mb-4">Perfect For</h3>
          <ul className="space-y-3 mb-8">
            {[
              'Personal sneaker collection',
              'Collectibles you\'re not selling',
              'Grails and rare pieces',
              'Items for future personal use',
              'Archived sold items with permanent records',
            ].map((item, idx) => (
              <li key={idx} className="flex items-center gap-3">
                <Lock className="w-4 h-4" style={{ color: '#8b5cf6' }} />
                <span className="text-sm" style={{ color: '#94a3b8' }}>{item}</span>
              </li>
            ))}
          </ul>

          <h3 className="text-xl font-semibold mb-4">Vault Features</h3>
          <div className="grid md:grid-cols-2 gap-4 mb-8">
            {[
              { icon: Archive, title: 'Permanent Archive', desc: 'Items in the vault are preserved with full history.' },
              { icon: Filter, title: 'Search & Filter', desc: 'Easily find items in your collection.' },
              { icon: RefreshCw, title: 'Restore Items', desc: 'Move items back to inventory if needed.' },
              { icon: Layers, title: 'Bulk Operations', desc: 'Manage multiple vault items at once.' },
            ].map((item, idx) => (
              <div key={idx} className="flex items-start gap-3 p-4 rounded-xl" style={{ backgroundColor: 'rgba(15, 30, 50, 0.5)', border: '1px solid rgba(59, 130, 246, 0.1)' }}>
                <item.icon className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#8b5cf6' }} />
                <div>
                  <h4 className="font-semibold mb-1">{item.title}</h4>
                  <p className="text-sm" style={{ color: '#94a3b8' }}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <InfoBox type="info">
            <p>Vault items don't count toward your inventory limits and won't appear in sales reports. They're completely separate from your business inventory.</p>
          </InfoBox>
        </>
      )
    },
    'reports': {
      title: 'Reports & Analytics',
      icon: BarChart3,
      content: (
        <>
          <ProBadge />
          <p className="mb-6" style={{ color: '#94a3b8' }}>
            Generate detailed reports to understand your business performance over time.
          </p>

          <h3 className="text-xl font-semibold mb-4">Available Reports</h3>
          <div className="space-y-4 mb-8">
            {[
              { title: 'Profit & Loss (P&L)', desc: 'Complete overview of revenue, costs, and net profit.' },
              { title: 'Sales Analysis', desc: 'Sales trends, top sellers, and platform performance.' },
              { title: 'Expense Breakdown', desc: 'Expenses by category with visual charts.' },
              { title: 'Inventory Report', desc: 'Stock levels, aging, and turnover analysis.' },
            ].map((report, idx) => (
              <div key={idx} className="flex items-start gap-3 p-4 rounded-xl" style={{ backgroundColor: 'rgba(15, 30, 50, 0.5)', border: '1px solid rgba(59, 130, 246, 0.1)' }}>
                <FileText className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#3b82f6' }} />
                <div>
                  <h4 className="font-semibold mb-1">{report.title}</h4>
                  <p className="text-sm" style={{ color: '#94a3b8' }}>{report.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <h3 className="text-xl font-semibold mb-4">Report Features</h3>
          <ul className="space-y-2 mb-6">
            {[
              'Filter by date range (daily, weekly, monthly, yearly, custom)',
              'Interactive charts and visualizations',
              'Export reports to PDF',
              'Compare performance across time periods',
            ].map((item, idx) => (
              <li key={idx} className="flex items-center gap-2 text-sm" style={{ color: '#94a3b8' }}>
                <BarChart3 className="w-4 h-4" style={{ color: '#3b82f6' }} />
                {item}
              </li>
            ))}
          </ul>
        </>
      )
    },
    'sales-reports': {
      title: 'Sales Reports',
      icon: DollarSign,
      content: (
        <>
          <ProBadge />
          <p className="mb-6" style={{ color: '#94a3b8' }}>
            Analyze your sales performance with detailed sales reports.
          </p>

          <h3 className="text-xl font-semibold mb-4">Sales Report Insights</h3>
          <div className="grid md:grid-cols-2 gap-4 mb-8">
            {[
              { title: 'Total Revenue', desc: 'Sum of all sales in the selected period.' },
              { title: 'Sales Count', desc: 'Number of items sold.' },
              { title: 'Average Sale Price', desc: 'Mean sale price across all sales.' },
              { title: 'Top Platforms', desc: 'Which platforms generate the most sales.' },
              { title: 'Sales Trends', desc: 'Daily, weekly, or monthly sales patterns.' },
              { title: 'Best Sellers', desc: 'Your top-performing items or categories.' },
            ].map((item, idx) => (
              <div key={idx} className="p-4 rounded-xl" style={{ backgroundColor: 'rgba(15, 30, 50, 0.5)', border: '1px solid rgba(59, 130, 246, 0.1)' }}>
                <h4 className="font-semibold mb-1">{item.title}</h4>
                <p className="text-sm" style={{ color: '#94a3b8' }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </>
      )
    },
    'profit-analysis': {
      title: 'Profit Analysis',
      icon: BarChart3,
      content: (
        <>
          <ProBadge />
          <p className="mb-6" style={{ color: '#94a3b8' }}>
            Understand your true profitability with detailed profit analysis.
          </p>

          <h3 className="text-xl font-semibold mb-4">Profit Metrics</h3>
          <div className="space-y-4 mb-8">
            {[
              { title: 'Gross Profit', desc: 'Revenue minus cost of goods (purchase price).' },
              { title: 'Net Profit', desc: 'Gross profit minus all fees and expenses.' },
              { title: 'Profit Margin', desc: 'Percentage of revenue that is profit.' },
              { title: 'ROI (Return on Investment)', desc: 'Profit relative to your initial investment.' },
            ].map((item, idx) => (
              <div key={idx} className="p-4 rounded-xl" style={{ backgroundColor: 'rgba(15, 30, 50, 0.5)', border: '1px solid rgba(59, 130, 246, 0.1)' }}>
                <h4 className="font-semibold mb-1">{item.title}</h4>
                <p className="text-sm" style={{ color: '#94a3b8' }}>{item.desc}</p>
              </div>
            ))}
          </div>

          <h3 className="text-xl font-semibold mb-4">Profit by Platform</h3>
          <p style={{ color: '#94a3b8' }}>
            See which selling platforms give you the best profit margins. This helps you decide where to focus your selling efforts.
          </p>
        </>
      )
    },
    'inventory-reports': {
      title: 'Inventory Reports',
      icon: PackageSearch,
      content: (
        <>
          <ProBadge />
          <p className="mb-6" style={{ color: '#94a3b8' }}>
            Get insights into your inventory performance and stock management.
          </p>

          <h3 className="text-xl font-semibold mb-4">Inventory Insights</h3>
          <div className="grid md:grid-cols-2 gap-4 mb-8">
            {[
              { title: 'Total Value', desc: 'The total value of your current inventory.' },
              { title: 'Item Count', desc: 'Number of items in stock.' },
              { title: 'Aging Analysis', desc: 'How long items have been in inventory.' },
              { title: 'Turnover Rate', desc: 'How quickly inventory sells.' },
            ].map((item, idx) => (
              <div key={idx} className="p-4 rounded-xl" style={{ backgroundColor: 'rgba(15, 30, 50, 0.5)', border: '1px solid rgba(59, 130, 246, 0.1)' }}>
                <h4 className="font-semibold mb-1">{item.title}</h4>
                <p className="text-sm" style={{ color: '#94a3b8' }}>{item.desc}</p>
              </div>
            ))}
          </div>

          <InfoBox type="info">
            <p>Use inventory aging reports to identify slow-moving items that may need price reductions or marketing attention.</p>
          </InfoBox>
        </>
      )
    },
    'exports': {
      title: 'Data Export',
      icon: Download,
      content: (
        <>
          <ProBadge />
          <p className="mb-6" style={{ color: '#94a3b8' }}>
            Export your data for backup, analysis, or tax purposes.
          </p>

          <h3 className="text-xl font-semibold mb-4">Export Options</h3>
          <div className="grid md:grid-cols-3 gap-4 mb-8">
            <div className="p-4 rounded-xl" style={{ backgroundColor: 'rgba(15, 30, 50, 0.5)', border: '1px solid rgba(59, 130, 246, 0.1)' }}>
              <h4 className="font-semibold mb-2">CSV</h4>
              <p className="text-sm" style={{ color: '#94a3b8' }}>Spreadsheet format for Excel or Google Sheets.</p>
            </div>
            <div className="p-4 rounded-xl" style={{ backgroundColor: 'rgba(15, 30, 50, 0.5)', border: '1px solid rgba(59, 130, 246, 0.1)' }}>
              <h4 className="font-semibold mb-2">PDF</h4>
              <p className="text-sm" style={{ color: '#94a3b8' }}>Formatted reports for printing or sharing.</p>
            </div>
            <div className="p-4 rounded-xl" style={{ backgroundColor: 'rgba(15, 30, 50, 0.5)', border: '1px solid rgba(59, 130, 246, 0.1)' }}>
              <h4 className="font-semibold mb-2">JSON</h4>
              <p className="text-sm" style={{ color: '#94a3b8' }}>Raw data format for technical use.</p>
            </div>
          </div>

          <h3 className="text-xl font-semibold mb-4">What Can You Export?</h3>
          <ul className="space-y-2 mb-6">
            {['Inventory list with all details', 'Sales records', 'Expense records', 'Package tracking data', 'Product database', 'Complete account data backup (ZIP)'].map((item, idx) => (
              <li key={idx} className="flex items-center gap-2 text-sm" style={{ color: '#94a3b8' }}>
                <Download className="w-4 h-4" style={{ color: '#22c55e' }} />
                {item}
              </li>
            ))}
          </ul>
        </>
      )
    },
    'csv-export': {
      title: 'CSV Export',
      icon: FileSpreadsheet,
      content: (
        <>
          <ProBadge />
          <p className="mb-6" style={{ color: '#94a3b8' }}>
            Export your data to CSV format for use in spreadsheet applications.
          </p>

          <h3 className="text-xl font-semibold mb-4">How to Export CSV</h3>
          <StepList steps={[
            { title: 'Go to the Page', desc: 'Navigate to Inventory, Sales, or Expenses page.' },
            { title: 'Click Export', desc: 'Find the Export button (usually near the top of the page).' },
            { title: 'Select CSV', desc: 'Choose CSV from the export options.' },
            { title: 'Download', desc: 'The file downloads to your device.' },
          ]} color="#22c55e" />

          <h3 className="text-xl font-semibold mb-4">CSV Contents</h3>
          <p className="mb-4" style={{ color: '#94a3b8' }}>
            CSV exports include all relevant columns for the data type:
          </p>
          <ul className="space-y-2 mb-6">
            {[
              'Inventory: Name, SKU, brand, condition, prices, dates, etc.',
              'Sales: Item, sale price, profit, platform, date, buyer',
              'Expenses: Name, amount, category, date, notes',
            ].map((item, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm" style={{ color: '#94a3b8' }}>
                <FileSpreadsheet className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#22c55e' }} />
                {item}
              </li>
            ))}
          </ul>
        </>
      )
    },
    'pdf-reports': {
      title: 'PDF Reports',
      icon: FileText,
      content: (
        <>
          <ProBadge />
          <p className="mb-6" style={{ color: '#94a3b8' }}>
            Generate professional PDF reports for printing, sharing, or record keeping.
          </p>

          <h3 className="text-xl font-semibold mb-4">PDF Report Features</h3>
          <ul className="space-y-3 mb-8">
            {[
              'Professional formatting with your data in clean tables',
              'Landscape orientation for wide data sets',
              'Properly formatted currency and dates',
              'Headers with report title and date range',
              'Perfect for tax records and business documentation',
            ].map((item, idx) => (
              <li key={idx} className="flex items-center gap-3">
                <FileText className="w-4 h-4" style={{ color: '#3b82f6' }} />
                <span className="text-sm" style={{ color: '#94a3b8' }}>{item}</span>
              </li>
            ))}
          </ul>

          <InfoBox type="info">
            <p className="font-medium mb-1">Tax Season Tip</p>
            <p>Generate monthly or yearly PDF reports of your sales and expenses to simplify tax preparation.</p>
          </InfoBox>
        </>
      )
    },

    // ==================== ACCOUNT & SETTINGS ====================
    'profile': {
      title: 'Profile Settings',
      icon: Settings,
      content: (
        <>
          <p className="mb-6" style={{ color: '#94a3b8' }}>
            Manage your account details and preferences from the Account Settings page.
          </p>

          <h3 className="text-xl font-semibold mb-4">Profile Options</h3>
          <div className="space-y-4 mb-8">
            {[
              { icon: Mail, title: 'Email Address', desc: 'Update your login email. Requires verification of the new email.' },
              { icon: Key, title: 'Password', desc: 'Change your password for account security.' },
              { icon: Camera, title: 'Profile Picture', desc: 'Upload a profile photo.' },
              { icon: Settings, title: 'Preferences', desc: 'Set currency, date format, and display preferences.' },
            ].map((item, idx) => (
              <div key={idx} className="flex items-start gap-3 p-4 rounded-xl" style={{ backgroundColor: 'rgba(15, 30, 50, 0.5)', border: '1px solid rgba(59, 130, 246, 0.1)' }}>
                <item.icon className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#3b82f6' }} />
                <div>
                  <h4 className="font-semibold mb-1">{item.title}</h4>
                  <p className="text-sm" style={{ color: '#94a3b8' }}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <h3 className="text-xl font-semibold mb-4">Display Preferences</h3>
          <ul className="space-y-2 mb-6">
            {[
              'Currency (USD, EUR, GBP, etc.)',
              'Date format preferences',
              'Theme (Light, Dark, or Auto)',
              'Default view (Table or Grid)',
            ].map((item, idx) => (
              <li key={idx} className="flex items-center gap-2 text-sm" style={{ color: '#94a3b8' }}>
                <ChevronRight className="w-4 h-4" style={{ color: '#3b82f6' }} />
                {item}
              </li>
            ))}
          </ul>
        </>
      )
    },
    'subscription': {
      title: 'Subscription Management',
      icon: CreditCard,
      content: (
        <>
          <p className="mb-6" style={{ color: '#94a3b8' }}>
            Manage your Nexus subscription from the Account Settings page.
          </p>

          <h3 className="text-xl font-semibold mb-4">Subscription Options</h3>
          <div className="grid md:grid-cols-2 gap-4 mb-8">
            {[
              { title: 'View Plan', desc: 'See your current plan and features.' },
              { title: 'Upgrade', desc: 'Upgrade from Free to Pro for full access.' },
              { title: 'Manage Billing', desc: 'Update payment method and view invoices.' },
              { title: 'Cancel', desc: 'Cancel your subscription anytime.' },
            ].map((item, idx) => (
              <div key={idx} className="p-4 rounded-xl" style={{ backgroundColor: 'rgba(15, 30, 50, 0.5)', border: '1px solid rgba(59, 130, 246, 0.1)' }}>
                <h4 className="font-semibold mb-1">{item.title}</h4>
                <p className="text-sm" style={{ color: '#94a3b8' }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </>
      )
    },
    'upgrading': {
      title: 'Upgrading to Pro',
      icon: Crown,
      content: (
        <>
          <p className="mb-6" style={{ color: '#94a3b8' }}>
            Upgrade to Nexus Pro for unlimited access and premium features.
          </p>

          <h3 className="text-xl font-semibold mb-4">How to Upgrade</h3>
          <StepList steps={[
            { title: 'Go to Settings', desc: 'Navigate to your Account Settings.' },
            { title: 'Find Subscription', desc: 'Scroll to the Subscription section.' },
            { title: 'Click Upgrade', desc: 'Choose a plan and click the upgrade button.' },
            { title: 'Complete Payment', desc: 'Enter payment details securely through Stripe.' },
            { title: 'Instant Access', desc: 'Paid features activate immediately!' },
          ]} color="#f59e0b" />

          <h3 className="text-xl font-semibold mb-4">What You Get</h3>
          <ul className="space-y-2 mb-6">
            {[
              'Unlimited inventory, sales, and expenses',
              'Real-time package tracking',
              'Vault for personal collection',
              'Advanced reports and analytics',
              'Data export (CSV, PDF, JSON)',
              'Priority support',
            ].map((item, idx) => (
              <li key={idx} className="flex items-center gap-2 text-sm" style={{ color: '#94a3b8' }}>
                <Crown className="w-4 h-4" style={{ color: '#f59e0b' }} />
                {item}
              </li>
            ))}
          </ul>

          <div className="p-4 rounded-xl" style={{ background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(29, 78, 216, 0.15) 100%)', border: '1px solid rgba(59, 130, 246, 0.3)' }}>
            <p className="text-lg font-semibold mb-2" style={{ color: '#60a5fa' }}>$15/month</p>
            <p className="text-sm" style={{ color: '#94a3b8' }}>Billed monthly. Cancel anytime.</p>
          </div>
        </>
      )
    },
    'billing': {
      title: 'Managing Billing',
      icon: CreditCard,
      content: (
        <>
          <p className="mb-6" style={{ color: '#94a3b8' }}>
            Manage your payment methods and view billing history.
          </p>

          <h3 className="text-xl font-semibold mb-4">Billing Management</h3>
          <StepList steps={[
            { title: 'Go to Settings', desc: 'Navigate to Account Settings.' },
            { title: 'Find Billing', desc: 'Click on "Manage Subscription" or "Billing".' },
            { title: 'Stripe Portal', desc: 'You\'ll be redirected to the secure Stripe billing portal.' },
            { title: 'Update Details', desc: 'Update payment method, view invoices, or download receipts.' },
          ]} color="#3b82f6" />

          <h3 className="text-xl font-semibold mb-4">What You Can Do</h3>
          <ul className="space-y-2 mb-6">
            {[
              'Update credit/debit card information',
              'View past invoices and receipts',
              'Download invoices for tax purposes',
              'See upcoming payment dates',
            ].map((item, idx) => (
              <li key={idx} className="flex items-center gap-2 text-sm" style={{ color: '#94a3b8' }}>
                <CreditCard className="w-4 h-4" style={{ color: '#3b82f6' }} />
                {item}
              </li>
            ))}
          </ul>

          <InfoBox type="info">
            <p>All payments are processed securely through Stripe. Nexus never stores your full credit card information.</p>
          </InfoBox>
        </>
      )
    },
    'cancellation': {
      title: 'Cancellation',
      icon: XCircle,
      content: (
        <>
          <p className="mb-6" style={{ color: '#94a3b8' }}>
            You can cancel your Pro subscription at any time.
          </p>

          <h3 className="text-xl font-semibold mb-4">How to Cancel</h3>
          <StepList steps={[
            { title: 'Go to Settings', desc: 'Navigate to Account Settings.' },
            { title: 'Find Subscription', desc: 'Locate the Subscription section.' },
            { title: 'Click Cancel', desc: 'Click "Cancel Subscription".' },
            { title: 'Confirm', desc: 'Follow the prompts to confirm cancellation.' },
          ]} color="#ef4444" />

          <h3 className="text-xl font-semibold mb-4">What Happens After Cancellation</h3>
          <ul className="space-y-3 mb-8">
            {[
              { icon: CheckCircle, color: '#22c55e', text: 'You keep Pro access until the end of your billing period' },
              { icon: CheckCircle, color: '#22c55e', text: 'All your data is preserved' },
              { icon: CheckCircle, color: '#22c55e', text: 'You can still use Free plan features' },
              { icon: AlertCircle, color: '#f59e0b', text: 'Pro features will be locked after the period ends' },
              { icon: AlertCircle, color: '#f59e0b', text: 'Data beyond Free limits becomes view-only' },
            ].map((item, idx) => (
              <li key={idx} className="flex items-center gap-3">
                <item.icon className="w-4 h-4" style={{ color: item.color }} />
                <span className="text-sm" style={{ color: '#94a3b8' }}>{item.text}</span>
              </li>
            ))}
          </ul>

          <InfoBox type="info">
            <p>You can resubscribe to Pro at any time to regain access to all features and your full data.</p>
          </InfoBox>
        </>
      )
    },
    'security': {
      title: 'Security',
      icon: Shield,
      content: (
        <>
          <p className="mb-6" style={{ color: '#94a3b8' }}>
            Keep your Nexus account secure with these security features.
          </p>

          <h3 className="text-xl font-semibold mb-4">Security Features</h3>
          <div className="grid md:grid-cols-2 gap-4 mb-8">
            {[
              { icon: Key, title: 'Strong Password', desc: 'Use a unique, complex password.' },
              { icon: Smartphone, title: 'Two-Factor Auth', desc: 'Add SMS verification for extra security.' },
              { icon: Shield, title: 'Secure Sessions', desc: 'View and manage active sessions.' },
              { icon: Lock, title: 'Data Encryption', desc: 'Your data is encrypted in transit and at rest.' },
            ].map((item, idx) => (
              <div key={idx} className="flex items-start gap-3 p-4 rounded-xl" style={{ backgroundColor: 'rgba(15, 30, 50, 0.5)', border: '1px solid rgba(59, 130, 246, 0.1)' }}>
                <item.icon className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#22c55e' }} />
                <div>
                  <h4 className="font-semibold mb-1">{item.title}</h4>
                  <p className="text-sm" style={{ color: '#94a3b8' }}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <h3 className="text-xl font-semibold mb-4">Session Management</h3>
          <p style={{ color: '#94a3b8' }}>
            View all devices and browsers where you're logged in. You can sign out of individual sessions
            or all sessions at once from the Account Settings page.
          </p>
        </>
      )
    },
    'password': {
      title: 'Password',
      icon: Key,
      content: (
        <>
          <p className="mb-6" style={{ color: '#94a3b8' }}>
            Keep your account secure by using a strong password and updating it regularly.
          </p>

          <h3 className="text-xl font-semibold mb-4">Changing Your Password</h3>
          <StepList steps={[
            { title: 'Go to Settings', desc: 'Navigate to Account Settings.' },
            { title: 'Find Security', desc: 'Scroll to the Password or Security section.' },
            { title: 'Enter Current Password', desc: 'Verify your identity with your current password.' },
            { title: 'Create New Password', desc: 'Enter and confirm your new password.' },
            { title: 'Save', desc: 'Click Save to update your password.' },
          ]} color="#22c55e" />

          <h3 className="text-xl font-semibold mb-4">Password Requirements</h3>
          <ul className="space-y-2 mb-6">
            {[
              'Minimum 8 characters',
              'Mix of uppercase and lowercase letters',
              'Include numbers and special characters',
              'Avoid common passwords or personal information',
            ].map((item, idx) => (
              <li key={idx} className="flex items-center gap-2 text-sm" style={{ color: '#94a3b8' }}>
                <CheckCircle className="w-4 h-4" style={{ color: '#22c55e' }} />
                {item}
              </li>
            ))}
          </ul>

          <h3 className="text-xl font-semibold mb-4">Forgot Password?</h3>
          <p style={{ color: '#94a3b8' }}>
            If you've forgotten your password, click "Forgot Password" on the login page to receive a
            password reset link via email.
          </p>
        </>
      )
    },
    'mfa': {
      title: 'Two-Factor Auth (MFA)',
      icon: Smartphone,
      content: (
        <>
          <p className="mb-6" style={{ color: '#94a3b8' }}>
            Add an extra layer of security to your account with SMS-based two-factor authentication.
          </p>

          <h3 className="text-xl font-semibold mb-4">What is MFA?</h3>
          <p className="mb-6" style={{ color: '#94a3b8' }}>
            Multi-Factor Authentication (MFA) requires both your password and a verification code sent
            to your phone when signing in. This prevents unauthorized access even if someone knows your password.
          </p>

          <h3 className="text-xl font-semibold mb-4">Setting Up MFA</h3>
          <StepList steps={[
            { title: 'Go to Security Settings', desc: 'Navigate to Account Settings > Security.' },
            { title: 'Click Enable MFA', desc: 'Start the MFA enrollment process.' },
            { title: 'Enter Phone Number', desc: 'Provide your mobile phone number.' },
            { title: 'Verify Code', desc: 'Enter the SMS code sent to your phone.' },
            { title: 'Complete Setup', desc: 'MFA is now enabled on your account.' },
          ]} color="#22c55e" />

          <h3 className="text-xl font-semibold mb-4">Signing In with MFA</h3>
          <p className="mb-4" style={{ color: '#94a3b8' }}>
            After entering your password, you'll receive an SMS with a verification code.
            Enter this code to complete sign-in. Codes are valid for 60 seconds.
          </p>

          <InfoBox type="warning">
            <p className="font-medium mb-1">Important</p>
            <p>Make sure your phone number is up to date. If you lose access to your phone, contact support for account recovery.</p>
          </InfoBox>
        </>
      )
    },

    // ==================== FAQ ====================
    'faq-general': {
      title: 'General Questions',
      icon: HelpCircle,
      content: (
        <>
          <p className="mb-6" style={{ color: '#94a3b8' }}>
            Common questions about using Nexus.
          </p>

          <div className="space-y-2">
            <FAQItem
              question="What is Nexus?"
              answer="Nexus is an all-in-one inventory management system designed for resellers, collectors, and small business owners. It helps you track inventory, record sales, manage expenses, and analyze your business performance."
            />
            <FAQItem
              question="Is Nexus free to use?"
              answer="Yes! Nexus offers a generous free plan that includes up to 150 inventory items, 50 sales, and 50 expenses. Upgrade to a paid plan for higher limits and additional features."
            />
            <FAQItem
              question="What platforms does Nexus support?"
              answer="Nexus supports all major reselling platforms including eBay, StockX, GOAT, Mercari, Poshmark, Facebook Marketplace, Depop, Grailed, Whatnot, and more. You can also track in-person sales and sales through social media."
            />
            <FAQItem
              question="Can I use Nexus on my phone?"
              answer="Yes! Nexus is fully responsive and works great on mobile devices. Access all features from your phone's web browser."
            />
            <FAQItem
              question="Is my data secure?"
              answer="Absolutely. Your data is encrypted in transit and at rest. We use industry-standard security practices and never store your full payment information. You can also enable two-factor authentication for extra security."
            />
            <FAQItem
              question="Can I import my existing inventory?"
              answer="Yes! You can import inventory, expenses, and packages from CSV files. Download our templates to ensure proper formatting, then upload your data."
            />
          </div>
        </>
      )
    },
    'faq-billing': {
      title: 'Billing & Subscriptions',
      icon: CreditCard,
      content: (
        <>
          <p className="mb-6" style={{ color: '#94a3b8' }}>
            Questions about Pro subscription and billing.
          </p>

          <div className="space-y-2">
            <FAQItem
              question="How much does Nexus cost?"
              answer="Nexus offers multiple plans: Starter ($7/mo), Pro ($15/mo), and Enterprise ($45/mo). All paid plans include package tracking, vault, reports, and data export. Higher tiers offer increased limits and additional features like API access."
            />
            <FAQItem
              question="How do I upgrade to Pro?"
              answer="Go to Account Settings, find the Subscription section, and choose a plan. Complete payment securely through Stripe, and paid features activate immediately."
            />
            <FAQItem
              question="Can I cancel my subscription anytime?"
              answer="Yes! You can cancel your Pro subscription at any time from Account Settings. You'll keep Pro access until the end of your current billing period."
            />
            <FAQItem
              question="What happens to my data if I cancel Pro?"
              answer="Your data is never deleted. After cancellation, you'll revert to the Free plan. Data beyond Free limits (150 items, 50 sales, 50 expenses) becomes view-only until you re-subscribe."
            />
            <FAQItem
              question="What payment methods do you accept?"
              answer="We accept all major credit and debit cards through Stripe, including Visa, Mastercard, American Express, and Discover."
            />
            <FAQItem
              question="Can I get a refund?"
              answer="We offer refunds on a case-by-case basis within 7 days of purchase. Contact support@nexusmanager.app with your request."
            />
          </div>
        </>
      )
    },
    'faq-troubleshooting': {
      title: 'Troubleshooting',
      icon: AlertCircle,
      content: (
        <>
          <p className="mb-6" style={{ color: '#94a3b8' }}>
            Solutions to common issues.
          </p>

          <div className="space-y-2">
            <FAQItem
              question="I can't log in to my account"
              answer={
                <div>
                  <p className="mb-2">Try these steps:</p>
                  <ul className="list-disc ml-4 space-y-1">
                    <li>Make sure you're using the correct email address</li>
                    <li>Use the "Forgot Password" link to reset your password</li>
                    <li>Check if your email is verified (check spam folder)</li>
                    <li>Clear your browser cache and cookies</li>
                    <li>Try a different browser or incognito mode</li>
                  </ul>
                </div>
              }
            />
            <FAQItem
              question="The barcode scanner isn't working"
              answer={
                <div>
                  <p className="mb-2">Check these common issues:</p>
                  <ul className="list-disc ml-4 space-y-1">
                    <li>Allow camera permissions when prompted</li>
                    <li>Ensure good lighting on the barcode</li>
                    <li>Hold the camera steady and close enough to the barcode</li>
                    <li>Try the manual entry option if camera doesn't work</li>
                    <li>On iOS, check Settings {'>'} Safari {'>'} Camera permissions</li>
                  </ul>
                </div>
              }
            />
            <FAQItem
              question="My data isn't syncing"
              answer="Nexus syncs data in real-time. If you're seeing sync issues, check your internet connection and refresh the page. If problems persist, try logging out and back in."
            />
            <FAQItem
              question="I'm getting an error when adding items"
              answer="Make sure all required fields are filled out correctly. Check that prices are valid numbers and dates are in the correct format. If the error persists, try refreshing the page or contact support."
            />
            <FAQItem
              question="Package tracking isn't updating"
              answer="Tracking updates depend on carrier data availability. Some carriers update less frequently than others. Try manually refreshing the tracking. If issues persist, the tracking number may be invalid or the carrier may be experiencing delays."
            />
            <FAQItem
              question="I can't access Pro features"
              answer="Make sure your subscription is active by checking Account Settings. If you recently subscribed, try logging out and back in. If issues persist, contact support with your account details."
            />
          </div>
        </>
      )
    },

    // ==================== CONTACT SUPPORT ====================
    'contact-support': {
      title: 'Get Help',
      icon: MessageSquare,
      content: (
        <>
          <p className="mb-6" style={{ color: '#94a3b8' }}>
            Need help? We're here for you. Reach out through any of these channels.
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* Email Support */}
            <div className="p-6 rounded-xl" style={{ backgroundColor: 'rgba(15, 30, 50, 0.5)', border: '1px solid rgba(59, 130, 246, 0.15)' }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)' }}>
                  <Mail className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold">Email Support</h4>
                  <p className="text-sm" style={{ color: '#64748b' }}>Usually responds within 24 hours</p>
                </div>
              </div>
              <a
                href="mailto:support@nexusmanager.app"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                style={{ backgroundColor: 'rgba(59, 130, 246, 0.15)', color: '#60a5fa' }}
              >
                <Mail className="w-4 h-4" />
                support@nexusmanager.app
              </a>
            </div>

            {/* Documentation */}
            <div className="p-6 rounded-xl" style={{ backgroundColor: 'rgba(15, 30, 50, 0.5)', border: '1px solid rgba(59, 130, 246, 0.15)' }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)' }}>
                  <Book className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold">Documentation</h4>
                  <p className="text-sm" style={{ color: '#64748b' }}>Browse guides and tutorials</p>
                </div>
              </div>
              <p className="text-sm" style={{ color: '#94a3b8' }}>
                You're already here! Use the sidebar to explore all documentation topics.
              </p>
            </div>
          </div>

          <h3 className="text-xl font-semibold mb-4">Before Contacting Support</h3>
          <p className="mb-4" style={{ color: '#94a3b8' }}>
            To help us resolve your issue faster, please include:
          </p>
          <ul className="space-y-2 mb-8">
            {[
              'Your account email address',
              'A clear description of the issue',
              'Steps to reproduce the problem',
              'Screenshots if applicable',
              'Your browser and device type',
            ].map((item, idx) => (
              <li key={idx} className="flex items-center gap-2 text-sm" style={{ color: '#94a3b8' }}>
                <ChevronRight className="w-4 h-4" style={{ color: '#22c55e' }} />
                {item}
              </li>
            ))}
          </ul>

          <h3 className="text-xl font-semibold mb-4">Priority Support</h3>
          <div className="p-4 rounded-xl flex items-start gap-3" style={{ background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(234, 88, 12, 0.15) 100%)', border: '1px solid rgba(245, 158, 11, 0.2)' }}>
            <Crown className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#f59e0b' }} />
            <div>
              <p className="font-medium mb-1">Pro subscribers get priority support</p>
              <p className="text-sm" style={{ color: '#94a3b8' }}>
                Upgrade to Pro for faster response times and dedicated support.
              </p>
            </div>
          </div>
        </>
      )
    },
  };

  // Filter docs based on search
  const filteredSections = searchQuery
    ? docSections.map(section => ({
        ...section,
        items: section.items.filter(item =>
          item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (docContent[item.id]?.title || '').toLowerCase().includes(searchQuery.toLowerCase())
        )
      })).filter(section => section.items.length > 0)
    : docSections;

  const currentDoc = docContent[activeDoc];

  // ========== MOBILE VIEW ==========
  if (isMobile) {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #0c1929 0%, #0a1420 100%)', color: '#ffffff' }}>
        {/* Mobile Header */}
        <header style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 50,
          backgroundColor: 'rgba(12, 25, 41, 0.9)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(59, 130, 246, 0.1)'
        }}>
          <nav style={{ padding: '0 16px', height: '56px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Link to="/" style={{ display: 'flex', alignItems: 'center', color: '#60a5fa' }}>
                <ArrowLeft style={{ width: '20px', height: '20px' }} />
              </Link>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Package style={{ width: '18px', height: '18px', color: '#fff' }} />
                </div>
                <span style={{ fontSize: '18px', fontWeight: 700 }}>Documentation</span>
              </div>
            </div>
          </nav>
        </header>

        {/* Mobile Content */}
        <main style={{ paddingTop: '72px', paddingBottom: '40px', paddingLeft: '20px', paddingRight: '20px' }}>
          {/* Search */}
          <div style={{ marginBottom: '24px', position: 'relative' }}>
            <Search style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', width: '18px', height: '18px', color: '#64748b' }} />
            <input
              type="text"
              placeholder="Search documentation..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 12px 12px 40px',
                borderRadius: '12px',
                backgroundColor: 'rgba(15, 30, 50, 0.5)',
                border: '1px solid rgba(59, 130, 246, 0.15)',
                color: '#fff',
                fontSize: '14px',
                outline: 'none'
              }}
            />
          </div>

          {/* Doc Sections */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {filteredSections.map((section) => (
              <div key={section.id}>
                <button
                  onClick={() => toggleSection(section.id)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px',
                    borderRadius: '12px',
                    backgroundColor: 'rgba(15, 30, 50, 0.5)',
                    border: '1px solid rgba(59, 130, 246, 0.15)',
                    color: '#fff',
                    cursor: 'pointer'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <section.icon style={{ width: '18px', height: '18px', color: '#3b82f6' }} />
                    <span style={{ fontWeight: 600, fontSize: '14px' }}>{section.title}</span>
                  </div>
                  <ChevronDown
                    style={{
                      width: '18px',
                      height: '18px',
                      color: '#64748b',
                      transform: expandedSections.includes(section.id) ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s'
                    }}
                  />
                </button>

                {expandedSections.includes(section.id) && (
                  <div style={{ marginTop: '8px', marginLeft: '12px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {section.items.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => setActiveDoc(item.id)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '10px 12px',
                          borderRadius: '8px',
                          backgroundColor: activeDoc === item.id ? 'rgba(59, 130, 246, 0.15)' : 'transparent',
                          border: 'none',
                          color: activeDoc === item.id ? '#60a5fa' : '#94a3b8',
                          fontSize: '14px',
                          width: '100%',
                          textAlign: 'left',
                          cursor: 'pointer'
                        }}
                      >
                        <ChevronRight style={{ width: '14px', height: '14px' }} />
                        {item.title}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Current Doc Content */}
          {currentDoc && (
            <div style={{ marginTop: '32px', padding: '20px', borderRadius: '16px', backgroundColor: 'rgba(15, 30, 50, 0.5)', border: '1px solid rgba(59, 130, 246, 0.15)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                <currentDoc.icon style={{ width: '24px', height: '24px', color: '#3b82f6' }} />
                <h2 style={{ fontSize: '20px', fontWeight: 700 }}>{currentDoc.title}</h2>
              </div>
              {currentDoc.content}
            </div>
          )}
        </main>

        {/* Mobile Footer */}
        <footer style={{
          padding: '24px 20px',
          borderTop: '1px solid rgba(59, 130, 246, 0.1)',
          textAlign: 'center'
        }}>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '24px', fontSize: '12px', color: '#64748b' }}>
            <Link to="/privacy" style={{ color: '#64748b', textDecoration: 'none' }}>Privacy</Link>
            <Link to="/terms" style={{ color: '#64748b', textDecoration: 'none' }}>Terms</Link>
            <Link to="/support" style={{ color: '#64748b', textDecoration: 'none' }}>Support</Link>
          </div>
        </footer>
      </div>
    );
  }

  // ========== DESKTOP VIEW ==========
  return (
    <div className="min-h-screen text-white" style={{ background: 'linear-gradient(180deg, #0c1929 0%, #0a1420 100%)' }}>
      {/* Navigation */}
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl" style={{ backgroundColor: 'rgba(12, 25, 41, 0.8)', borderBottom: '1px solid rgba(59, 130, 246, 0.1)' }}>
        <nav className="max-w-6xl mx-auto px-6">
          <div className="flex items-center justify-between h-16 relative">
            <Link to="/" className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)' }}>
                <Package className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold">Nexus</span>
            </Link>
            <div className="hidden md:flex items-center gap-8 absolute left-1/2 -translate-x-1/2">
              <Link to="/#features" className="text-sm text-blue-200/60 hover:text-white transition-colors">Features</Link>
              <Link to="/#pricing" className="text-sm text-blue-200/60 hover:text-white transition-colors">Pricing</Link>
              <Link to="/docs" className="text-sm text-white transition-colors">Docs</Link>
              <Link to="/support" className="text-sm text-blue-200/60 hover:text-white transition-colors">Support</Link>
            </div>
            <div className="flex items-center gap-2">
              <Link to="/?auth=login" className="px-4 py-2 text-sm font-medium text-blue-200/80 hover:text-white transition-colors">
                Sign In
              </Link>
              <Link to="/?auth=signup" className="px-5 py-2.5 text-sm font-semibold text-white rounded-full transition-all hover:-translate-y-0.5" style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)', boxShadow: '0 8px 32px rgba(59, 130, 246, 0.35)' }}>
                Get Started
              </Link>
            </div>
          </div>
        </nav>
      </header>

      {/* Content */}
      <main className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex gap-8">
            {/* Sidebar */}
            <aside className="w-72 flex-shrink-0">
              <div className="sticky top-24">
                {/* Search */}
                <div className="relative mb-6">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#64748b' }} />
                  <input
                    type="text"
                    placeholder="Search docs..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl text-sm outline-none"
                    style={{
                      backgroundColor: 'rgba(15, 30, 50, 0.5)',
                      border: '1px solid rgba(59, 130, 246, 0.15)',
                      color: '#fff'
                    }}
                  />
                </div>

                {/* Navigation */}
                <nav className="space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto pr-2">
                  {filteredSections.map((section) => (
                    <div key={section.id}>
                      <button
                        onClick={() => toggleSection(section.id)}
                        className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-white/5 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <section.icon className="w-4 h-4" style={{ color: '#3b82f6' }} />
                          <span className="text-sm font-semibold">{section.title}</span>
                        </div>
                        <ChevronDown
                          className="w-4 h-4 transition-transform"
                          style={{
                            color: '#64748b',
                            transform: expandedSections.includes(section.id) ? 'rotate(180deg)' : 'rotate(0deg)'
                          }}
                        />
                      </button>

                      {expandedSections.includes(section.id) && (
                        <div className="mt-1 ml-6 space-y-1">
                          {section.items.map((item) => (
                            <button
                              key={item.id}
                              onClick={() => setActiveDoc(item.id)}
                              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors text-left"
                              style={{
                                backgroundColor: activeDoc === item.id ? 'rgba(59, 130, 246, 0.15)' : 'transparent',
                                color: activeDoc === item.id ? '#60a5fa' : '#94a3b8'
                              }}
                            >
                              <ChevronRight className="w-3 h-3" />
                              {item.title}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Main Content */}
            <div className="flex-1 min-w-0">
              {currentDoc && (
                <article className="p-8 rounded-2xl" style={{ backgroundColor: 'rgba(15, 30, 50, 0.5)', border: '1px solid rgba(59, 130, 246, 0.15)' }}>
                  <div className="flex items-center gap-3 mb-6">
                    <currentDoc.icon className="w-7 h-7" style={{ color: '#3b82f6' }} />
                    <h1 className="text-2xl font-bold">{currentDoc.title}</h1>
                  </div>
                  <div className="prose prose-invert max-w-none">
                    {currentDoc.content}
                  </div>
                </article>
              )}

              {/* Still need help */}
              <div className="mt-8 p-6 rounded-2xl text-center" style={{ background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(29, 78, 216, 0.15) 100%)', border: '1px solid rgba(59, 130, 246, 0.2)' }}>
                <MessageSquare className="w-8 h-8 mx-auto mb-3" style={{ color: '#3b82f6' }} />
                <h3 className="text-lg font-semibold mb-2">Still have questions?</h3>
                <p className="text-sm mb-4" style={{ color: '#94a3b8' }}>Can't find what you're looking for? Our support team is here to help.</p>
                <Link
                  to="/support"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm transition-all hover:-translate-y-0.5"
                  style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)', boxShadow: '0 8px 32px rgba(59, 130, 246, 0.35)' }}
                >
                  Contact Support
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8" style={{ borderTop: '1px solid rgba(59, 130, 246, 0.1)' }}>
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)' }}>
              <Package className="w-4 h-4 text-white" />
            </div>
            <span className="font-semibold">Nexus</span>
          </div>
          <p className="text-sm" style={{ color: '#64748b' }}>
            © {new Date().getFullYear()} Nexus. All rights reserved.
          </p>
          <div className="flex items-center gap-6 text-sm" style={{ color: '#64748b' }}>
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
            <Link to="/docs" style={{ color: '#60a5fa' }}>Docs</Link>
            <Link to="/support" className="hover:text-white transition-colors">Support</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DocsPage;
